import {FC, FormEvent, useEffect, useState} from 'react'
import Select from 'react-select'
import {Filter} from 'react-feather'
import * as API from '../../api'
import BtnClose from '../../components/BtnClose'
import ReportFilter from './ReportFilter'
import {FilterContainer} from './shared/FilterStyles'
import {
  LojasListFilter,
  LojasRelacionamentoFilters,
  LojasRelacionamentoOptions,
  LojasRelacionamentoType,
  restoreLojasFilters,
  validateLojasFilters,
} from './lojasRelacionamento'

type Props = {
  type: LojasRelacionamentoType
  data: LojasRelacionamentoFilters | null
  onChange: (filters: LojasRelacionamentoFilters) => void
  onClose: () => void
}

const LojasRelacionamentoFilterModal: FC<Props> = ({type, data, onChange, onClose}) => {
  const [draft, setDraft] = useState(() => restoreLojasFilters(data))
  const [options, setOptions] = useState<LojasRelacionamentoOptions | null>(null)
  const [loading, setLoading] = useState(true)
  const [optionsError, setOptionsError] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [retry, setRetry] = useState(0)

  useEffect(() => {
    let active = true
    setLoading(true)
    setOptionsError(false)
    API.getLojasRelacionamentoOptions(type)
      .then((response) => {
        if (!response?.success || !response.data) throw new Error('Falha nas opções')
        if (active) setOptions(response.data)
      })
      .catch(() => {
        if (active) setOptionsError(true)
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [type, retry])

  const update = <K extends keyof LojasRelacionamentoFilters>(
    key: K,
    value: LojasRelacionamentoFilters[K]
  ) => {
    setDraft((current) => ({...current, [key]: value}))
    setValidationError(null)
  }

  const select = (key: LojasListFilter, label: string, values: string[] = []) => (
    <div className='form-group'>
      <label className='form-label' htmlFor={`${type}-${key}`}>
        {label}
      </label>
      <Select
        inputId={`${type}-${key}`}
        isMulti
        isLoading={loading}
        isDisabled={loading || optionsError}
        options={values.map((value) => ({value, label: value}))}
        value={draft[key].map((value) => ({value, label: value}))}
        placeholder={loading ? 'Carregando opções...' : `Selecione ${label}`}
        noOptionsMessage={() => 'Nenhuma opção disponível'}
        closeMenuOnSelect={false}
        onChange={(selected) =>
          update(
            key,
            selected.map((item) => item.value)
          )
        }
        className='select-container'
        classNamePrefix='select'
      />
    </div>
  )

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const error = validateLojasFilters(draft)
    setValidationError(error)
    if (!error) onChange(draft)
  }

  return (
    <FilterContainer>
      <div className='modal-container'>
        <div className='modal-header'>
          <h2 className='modal-title d-flex align-items-center gap-3 mb-0'>
            <Filter size={24} className='flex-shrink-0' />
            <span>Filtros para o relatório</span>
          </h2>
          <BtnClose onClick={onClose} />
        </div>
        <div className='form-section'>
          <h3 className='fs-5 fw-bold'>Filtros salvos</h3>
          <ReportFilter
            key={type}
            name={type}
            filter={draft}
            setFilter={(saved: any) => {
              if (saved?.data) {
                setDraft(restoreLojasFilters(saved.data))
                setValidationError(null)
              }
            }}
          />
          <form onSubmit={submit} className='mt-7'>
            {optionsError && (
              <div role='alert' className='alert alert-danger'>
                Não foi possível carregar as listas de filtros.
                <button
                  type='button'
                  className='btn btn-sm btn-light-danger ms-3'
                  onClick={() => setRetry((value) => value + 1)}
                >
                  Tentar novamente
                </button>
              </div>
            )}
            <div className='form-grid'>
              {select('industries', 'Indústria', options?.industries)}
              {select('distributors', 'Distribuidor', options?.distributors)}
              <div className='form-group'>
                <label className='form-label' htmlFor={`${type}-cnpj`}>
                  CNPJ Lojista
                </label>
                <input
                  id={`${type}-cnpj`}
                  className='form-control'
                  value={draft.cnpj}
                  placeholder='CNPJ completo ou parcial'
                  maxLength={18}
                  onChange={(event) => update('cnpj', event.target.value.toUpperCase())}
                />
              </div>
              <div className='form-group'>
                <label className='form-label' htmlFor={`${type}-nome`}>
                  Nome Lojista
                </label>
                <input
                  id={`${type}-nome`}
                  className='form-control'
                  value={draft.nomeLojista}
                  onChange={(event) => update('nomeLojista', event.target.value)}
                />
              </div>
              {select('estadoLojista', 'Estado Lojista', options?.estados)}
              {select('cidadeLojista', 'Cidade Lojista', options?.cidades)}
              {select('segmentoLojista', 'Segmento Lojista', options?.segmentos)}
              {select('tamanhoUL', 'Tamanho UL', options?.tamanhosUL)}
              <fieldset className='form-group'>
                <legend className='form-label'>Valor do último pedido</legend>
                <div className='d-flex gap-3'>
                  <div className='w-50'>
                    <label className='form-label' htmlFor={`${type}-minimum`}>
                      Mínimo (R$)
                    </label>
                    <input
                      id={`${type}-minimum`}
                      className='form-control'
                      inputMode='decimal'
                      value={draft.valorUltimoPedidoMin}
                      placeholder='Sem limite'
                      onChange={(event) => update('valorUltimoPedidoMin', event.target.value)}
                    />
                  </div>
                  <div className='w-50'>
                    <label className='form-label' htmlFor={`${type}-maximum`}>
                      Máximo (R$)
                    </label>
                    <input
                      id={`${type}-maximum`}
                      className='form-control'
                      inputMode='decimal'
                      value={draft.valorUltimoPedidoMax}
                      placeholder='Sem limite'
                      onChange={(event) => update('valorUltimoPedidoMax', event.target.value)}
                    />
                  </div>
                </div>
              </fieldset>
              <fieldset className='form-group'>
                <legend className='form-label'>Cliente exclusivo UL</legend>
                <div className='radio-group'>
                  {[
                    ['', 'Todos'],
                    ['Sim', 'Sim'],
                    ['Não', 'Não'],
                  ].map(([value, label]) => (
                    <label key={value} className='radio-item'>
                      <input
                        type='radio'
                        name={`${type}-exclusive`}
                        value={value}
                        checked={draft.apenasUnilever90Dias === value}
                        onChange={() => update('apenasUnilever90Dias', value)}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </fieldset>
              {type === 'lojas_quase_churn' &&
                select('faixaRisco', 'Risco de Churn', options?.faixasRisco)}
            </div>
            {validationError && (
              <div role='alert' className='alert alert-danger'>
                {validationError}
              </div>
            )}
            <button type='submit' className='filter-btn' disabled={loading || optionsError}>
              <Filter size={18} />
              {loading ? 'Carregando filtros...' : 'Filtrar'}
            </button>
          </form>
        </div>
      </div>
    </FilterContainer>
  )
}

export default LojasRelacionamentoFilterModal
